// Reject archive entry paths that would escape the receipt root (tar-slip /
// zip-slip). Archive uploads come from untrusted sources, so an entry like
// `rec/evidence/../receipt.json` must be rejected rather than silently
// normalized over the real receipt.json in the virtual filesystem. Mirrors the
// CLI's safeJoin guard in src/lib/package-archive.ts.
export function isUnsafeArchivePath(name: string): boolean {
  const slashed = name.replace(/\\/g, "/");
  if (slashed.startsWith("/")) return true; // absolute path
  return slashed.split("/").some((segment) => segment === "..");
}

// Detect the single top-level directory that wraps every entry (the
// receipt-library packs entries as `<receiptId>/...`). Returns the root with a
// trailing slash ONLY when every entry shares one first segment; returns "" if
// any entry sits at the top level or entries span multiple roots. This is what
// keeps a top-level `receipt.json` from being silently ignored while a wrapped
// `rec/receipt.json` is stripped onto the same path — which would make the
// chosen receipt depend on archive entry order.
export function detectCommonRoot(names: string[]): string {
  if (names.length === 0) return "";
  const tops = new Set<string>();
  for (const name of names) {
    const normalized = name.replace(/^\/+/, "");
    const slash = normalized.indexOf("/");
    if (slash <= 0) return ""; // a top-level file → no common root to strip
    tops.add(normalized.slice(0, slash));
  }
  return tops.size === 1 ? `${Array.from(tops)[0]}/` : "";
}

// Collapse `.` and empty segments in an already-root-stripped, traversal-checked
// path so equivalent spellings (`a/./b`, `a//b`, a leading `./`) map to a single
// key — matching how the virtual filesystem normalizes paths. Without this, a
// duplicate guard misses order-dependent overwrites of the same logical file.
// (`..` is rejected earlier by isUnsafeArchivePath, so it can't appear here.)
export function normalizeArchivePath(relativePath: string): string {
  return relativePath
    .split("/")
    .filter((segment) => segment !== "" && segment !== ".")
    .join("/");
}
